import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledCertSection = styled.section`
  max-width: 900px;
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StyledCertCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 20px 22px;
  border-radius: var(--border-radius);
  background-color: var(--light-navy);
  transition: var(--transition);
  border-top: 2px solid transparent;

  &:hover {
    transform: translateY(-4px);
    border-top-color: var(--green);
  }

  .cert-year {
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    color: var(--green);
    margin-bottom: 6px;
  }

  .cert-name {
    color: var(--lightest-slate);
    font-size: var(--fz-md);
    font-weight: 500;
    line-height: 1.4;
    flex-grow: 1;
    margin-bottom: 10px;
  }

  .cert-meta {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .cert-link {
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    color: var(--green);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &::before {
      content: '↗ ';
    }
  }

  .cert-expired {
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    color: var(--slate);
  }
`;

const certifications = [
  {
    year: '2026/05',
    name: 'AWS Certified Generative AI Developer – Professional (AIP-C01)',
    link: null,
  },
  {
    year: '2026/03',
    name: 'Certified Kubernetes Administrator (CKA)',
    link: null,
  },
  {
    year: '2025/11',
    name: '第一種運転免許（普通自動車）',
    link: null,
  },
  {
    year: '2024/12',
    name: 'JLPT N1（日本語能力試験 一級）',
    link: 'https://zlypublication.s3.ap-northeast-1.amazonaws.com/jlptN1.pdf',
    linkLabel: '証明書',
  },
  {
    year: '2024/11',
    name: 'AWS Certified Machine Learning – Specialty',
    link: 'https://www.credly.com/badges/6254ab1b-0e14-4529-96a0-5db71d584096/linked_in_profile',
    linkLabel: 'Credly',
  },
  {
    year: '2021/11',
    name: 'Google Data Analytics Professional Certificate',
    link: 'https://www.coursera.org/account/accomplishments/professional-cert/Y57ULWYPSMFM',
    linkLabel: 'Coursera',
  },
  {
    year: '2021/10',
    name: '基本情報技術者試験（FE）合格',
    link: null,
  },
  {
    year: '2021/09',
    name: 'AWS Certified Solutions Architect – Associate',
    link: null,
    expired: true,
  },
];

const Certifications = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledCertSection id="certifications" ref={revealContainer}>
      <h2 className="numbered-heading">Certifications</h2>

      <StyledGrid>
        {certifications.map((cert, i) => (
          <StyledCertCard key={i}>
            <p className="cert-year">{cert.year}</p>
            <p className="cert-name">{cert.name}</p>
            <div className="cert-meta">
              {cert.link && (
                <a
                  href={cert.link}
                  className="cert-link"
                  target="_blank"
                  rel="noreferrer">
                  {cert.linkLabel || '証明書'}
                </a>
              )}
              {cert.expired && <span className="cert-expired">（期限切れ）</span>}
            </div>
          </StyledCertCard>
        ))}
      </StyledGrid>
    </StyledCertSection>
  );
};

export default Certifications;
